import React, { useState } from 'react';
import { Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';
import type { WesFilters } from '../useWesPalletStagingFilters';
import type { WesTabKey } from '../wesPalletStagingStaticData';
import { WesChartConfigToggles } from './WesChartConfig';
import { WesFilterFields, WesQuickSelects } from './WesFilterBar';

interface WesMobileFilterSheetProps {
  filters: WesFilters;
  activeTab: WesTabKey;
}

const activeFilterCount = (filters: WesFilters) =>
  Object.values(filters.applied).filter((ids) => ids.length > 0).length;

// Mobile: a Filters button with a bottom sheet, plus a sideways-scrolling quick-select row below it. Chart toggles are inside the sheet and only take effect on Apply.
const WesMobileFilterSheet: React.FC<WesMobileFilterSheetProps> = ({ filters, activeTab }) => {
  const [open, setOpen] = useState(false);
  const hasChartConfig = activeTab === 'executive-overview';
  const count = activeFilterCount(filters);
  const label = `${hasChartConfig ? 'Filters / Charts' : 'Filters'} (${count})`;

  const close = () => {
    filters.discardDraft();
    setOpen(false);
  };

  return (
    <View style={styles.wrap} testID="wes-mobile-filters">
      <TouchableOpacity
        style={styles.button}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={label}
        testID="wes-mobile-filter-btn"
      >
        <MaterialIcons name="filter-list" size={18} color="#2D2D2D" />
        <Text style={styles.buttonText}>{label}</Text>
      </TouchableOpacity>

      <View style={styles.quickWrap}>
        <WesQuickSelects filters={filters} scrollable />
      </View>

      <Modal visible={open} transparent animationType="slide" onRequestClose={close}>
        <View style={styles.overlay}>
          <View style={styles.sheet} testID="wes-mobile-sheet">
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>{hasChartConfig ? 'Filters and Charts' : 'Filters'}</Text>
              <TouchableOpacity onPress={close} accessibilityRole="button" accessibilityLabel="Close filters" testID="wes-sheet-close">
                <Text style={styles.close}>Close</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.sheetBody} keyboardShouldPersistTaps="handled">
              <WesFilterFields filters={filters} stacked />
              {hasChartConfig ? (
                <View style={styles.chartBlock} testID="wes-sheet-chart-config">
                  <Text style={styles.chartTitle}>Chart Configuration</Text>
                  <WesChartConfigToggles
                    hierarchy={filters.chart.shownHierarchy}
                    timeframe={filters.chart.shownTimeframe}
                    onHierarchyChange={(hierarchy) => filters.setChartToggle({ hierarchy }, false)}
                    onTimeframeChange={(timeframe) => filters.setChartToggle({ timeframe }, false)}
                  />
                </View>
              ) : null}
            </ScrollView>
            <View style={styles.footer}>
              <TouchableOpacity
                style={styles.resetBtn}
                onPress={() => {
                  filters.reset();
                  setOpen(false);
                }}
                testID="wes-sheet-reset"
              >
                <Text style={styles.resetText}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.applyBtn, filters.isDirty ? styles.applyEnabled : styles.applyDisabled]}
                disabled={!filters.isDirty}
                accessibilityState={{ disabled: !filters.isDirty }}
                onPress={() => {
                  filters.apply();
                  setOpen(false);
                }}
                testID="wes-sheet-apply"
              >
                <Text style={[styles.applyText, filters.isDirty && styles.applyTextEnabled]}>Apply</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { marginHorizontal: 24, marginTop: 12, rowGap: 10 },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    columnGap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#D0D9E8',
    backgroundColor: '#FFFFFF',
  },
  buttonText: { fontSize: 14, fontFamily: FontFamily.medium, color: '#2D2D2D' },
  quickWrap: { marginHorizontal: -24, paddingHorizontal: 24 },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: '#FFFFFF', borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '90%' },
  sheetHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16 },
  sheetTitle: { fontSize: 16, fontFamily: FontFamily.bold, color: '#1A1D21' },
  close: { fontSize: 14, fontFamily: FontFamily.medium, color: '#005CBC' },
  sheetBody: { paddingHorizontal: 16 },
  chartBlock: { marginTop: 16, rowGap: 8 },
  chartTitle: { fontSize: 11, fontFamily: FontFamily.bold, color: '#6B7280', textTransform: 'uppercase' },
  footer: { flexDirection: 'row', columnGap: 10, paddingHorizontal: 16, paddingTop: 12, paddingBottom: 20, borderTopWidth: 1, borderTopColor: '#E5E7EB' },
  resetBtn: {
    flex: 1,
    minHeight: 42,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#0B5FC6',
    backgroundColor: '#FFFFFF',
  },
  resetText: { color: '#0B5FC6', fontFamily: FontFamily.bold, fontSize: 14 },
  applyBtn: { flex: 1, minHeight: 42, alignItems: 'center', justifyContent: 'center', borderRadius: 8 },
  applyEnabled: { backgroundColor: '#0B5FC6' },
  applyDisabled: { backgroundColor: '#D0D5DD' },
  applyText: { color: '#9CA3AF', fontFamily: FontFamily.bold, fontSize: 14 },
  applyTextEnabled: { color: '#FFFFFF' },
});

export default WesMobileFilterSheet;
